import React, { useMemo, useState } from 'react'
import { LayoutChangeEvent, StyleSheet, Text, View } from 'react-native'
import { useQuery } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'

import { useStore } from '../../models'
import { DS } from '../../design-system'
import { fontFamily } from '../../theme'
import { formatPlayDate } from '../../utils/dateFormatter'
import Chip from './assets/Chip.svg'

const DAY_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'] // indexed by Date.getDay()

const COMPACT_CHIP_SIZE = 28
// when compact chips must overlap to fit, keep at least this much of each chip visible
const MIN_CHIP_VISIBLE = 8

function buildWindowDates(todayDate: string): string[] {
  const [y, m, d] = todayDate.split('-').map(Number)
  const dates: Array<{ str: string; dow: number }> = []
  for (let i = 6; i >= 0; i--) {
    const dt = new Date(y, m - 1, d - i)
    dates.push({
      str: `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`,
      dow: dt.getDay()
    })
  }
  return dates.sort((a, b) => a.dow - b.dow).map((x) => x.str)
}

export const Streaks = ({ compact = false }: { compact?: boolean }) => {
  const { user } = useStore().session
  const userId = user?.auth0Id || 'guest'
  const todayDate = useMemo(() => formatPlayDate(), [])
  const todayDow = useMemo(() => {
    const [y, m, d] = todayDate.split('-').map(Number)
    return new Date(y, m - 1, d).getDay()
  }, [todayDate])

  // Compute dates client-side so circles render immediately (no loading flicker)
  const displayDates = useMemo(() => buildWindowDates(todayDate), [todayDate])

  const activity = useQuery(api.games.queries.getWeeklyActivity, user?.auth0Id ? { userId, todayDate } : 'skip')

  const activeDatesSet = useMemo(() => new Set(activity?.activeDates ?? []), [activity?.activeDates])

  // Compact mode only: when the chips do not fit the available width, overlap
  // them just enough (capped so every chip stays partially visible).
  const [containerWidth, setContainerWidth] = useState<number | null>(null)
  const onLayout = (e: LayoutChangeEvent) => setContainerWidth(e.nativeEvent.layout.width)

  const overlap = useMemo(() => {
    if (!compact || containerWidth == null) return 0
    const chipCount = displayDates.length
    const needed = chipCount * COMPACT_CHIP_SIZE
    const deficit = needed - containerWidth
    if (deficit <= 0) return 0
    return Math.min(Math.ceil(deficit / (chipCount - 1)), COMPACT_CHIP_SIZE - MIN_CHIP_VISIBLE)
  }, [compact, containerWidth, displayDates.length])

  return (
    <View>
      <View style={styles.container} onLayout={compact ? onLayout : undefined}>
        {displayDates.map((date, index) => {
          const [y, m, d] = date.split('-').map(Number)
          const dow = new Date(y, m - 1, d).getDay()
          const dayLetter = DAY_LETTERS[dow]
          const isActive = activeDatesSet.has(date)
          const isPastWeek = dow > todayDow
          return (
            <View
              key={`StreakDay-${y}-${m}-${d}`}
              style={[
                styles.chipContainer,
                compact && styles.chipContainerCompact,
                isPastWeek && styles.chipContainerFaded,
                // overlapped chips stack left-over-right
                overlap > 0 && { zIndex: displayDates.length - index },
                index > 0 && overlap > 0 && { marginLeft: -overlap }
              ]}
            >
              {isActive ? <Chip width={compact ? 28 : undefined} height={compact ? 28 : undefined} /> : null}
              <Text style={[styles.text, compact && styles.textCompact]}>{dayLetter}</Text>
            </View>
          )
        })}
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  chipContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    // opaque fill matching the screen background: overlapped chips must look
    // solid so neighbors' borders do not show through
    backgroundColor: DS.palette['Brand/Cream'],
    borderWidth: 1,
    borderColor: 'lightgray',
    borderRadius: 50,
    height: 40,
    width: 40
  },
  chipContainerFaded: {
    opacity: 0.5
  },
  chipContainerCompact: {
    height: 28,
    width: 28
  },
  textCompact: {
    fontSize: 10
  },
  text: {
    fontFamily: fontFamily.GTAmericaStandardRegular,
    fontWeight: 'bold',
    textAlign: 'center',
    position: 'absolute'
  }
})
